import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  FiCheckCircle,
  FiAlertTriangle,
  FiWifi,
  FiRefreshCw,
  FiCrosshair,
} from "react-icons/fi";
import toast from "react-hot-toast";
import { Card, Loading } from "../components/ui";
import { AppBar } from "../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../components/desktopUi";
import CameraCapture from "../components/CameraCapture";
import QrScanner from "../components/QrScanner";
import CheckInMap from "../components/CheckInMap";
import {
  completeCheckinApi,
  getCheckinPlacesApi,
  getSettingApi,
  startCheckinApi,
  uploadImageApi,
  verifyCheckinImageApi,
  verifyCheckinPlaceApi,
  verifyCheckinQrCodeApi,
} from "../api/attendance.api";
import { extractErrorMessage } from "../api/client";
import { getCurrentPosition, getValidPlaces } from "../utils/geo";
import {
  CHECK_IN_RADIUS_DEFAULT,
  CHECK_IN_TYPE,
  EMP_BASE_PATH,
  EMP_SETTINGS,
} from "../config";

// Tông nút gradient xanh của app (gradientColors.primary2)
const BLUE_GRADIENT = "linear(to-b, #68A2FF, #2168DA)";
const PRIMARY = "#2168DA";

// Progress step 2 bước khớp app: "Check-in vị trí" (xong) -> bước 2
const ProgressStep = ({ step2Label }) => (
  <Flex align="center" justify="center" py="8px" pb="16px" bg="white">
    <Flex flex="1" justify="flex-end" align="center" gap="7px">
      <Box as={FiCheckCircle} color="#18C758" fontSize="24px" />
      <Text fontSize="14px" fontWeight={600} color="#08142D">
        Check-in vị trí
      </Text>
    </Flex>

    <Box w="40px" h="2px" borderRadius="30px" bg="#E1E1E1" mx="10px" />

    <Flex flex="1" justify="flex-start" align="center" gap="7px">
      <Flex w="24px" h="24px" borderRadius="full" bg={PRIMARY} align="center" justify="center">
        <Text fontSize="12px" fontWeight={700} color="white">
          2
        </Text>
      </Flex>
      <Text fontSize="14px" fontWeight={600} color="#99A4B8">
        {step2Label}
      </Text>
    </Flex>
  </Flex>
);

const CheckIn = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState("locating"); // locating|map|verifying|image|qr|bssid|completing|success|error
  const [error, setError] = useState("");
  const [busyMsg, setBusyMsg] = useState("");
  const [place, setPlace] = useState(null); // địa điểm đã verify (có checkInTypes)

  const [userLoc, setUserLoc] = useState(null); // { lat, lng }
  const [places, setPlaces] = useState([]);
  const [radius, setRadius] = useState(CHECK_IN_RADIUS_DEFAULT);
  const [defaultId, setDefaultId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [reloading, setReloading] = useState(false);

  const validPlaces = useMemo(
    () => (userLoc ? getValidPlaces(userLoc, places, radius) : []),
    [userLoc, places, radius]
  );
  const validIds = useMemo(() => new Set(validPlaces.map((p) => p._id)), [validPlaces]);

  // Tự chọn địa điểm gần nhất khi danh sách hợp lệ thay đổi
  useEffect(() => {
    setSelectedId((prev) =>
      prev && validPlaces.some((p) => p._id === prev)
        ? prev
        : validPlaces[0]?._id || null
    );
  }, [validPlaces]);

  const fail = useCallback((e) => {
    setError(typeof e === "string" ? e : extractErrorMessage(e));
    setPhase("error");
  }, []);

  const loadMeta = useCallback(async () => {
    const [placesRes, radiusRes, defRes] = await Promise.all([
      getCheckinPlacesApi({ page: 1, limit: 200, status: true }).catch(() => null),
      getSettingApi(EMP_SETTINGS.CHECKIN_RADIUS).catch(() => null),
      getSettingApi(EMP_SETTINGS.CHECKIN_PLACE_DEFAULT_ID).catch(() => null),
    ]);
    setPlaces(placesRes?.items || placesRes || []);
    const r = Number(radiusRes?.value);
    setRadius(Number.isFinite(r) && r > 0 ? r : CHECK_IN_RADIUS_DEFAULT);
    setDefaultId(defRes?.value || null);
  }, []);

  const begin = useCallback(async () => {
    setError("");
    setPlace(null);
    setPhase("locating");
    setBusyMsg("Đang lấy vị trí & dữ liệu chấm công...");
    try {
      const [loc] = await Promise.all([getCurrentPosition(), loadMeta()]);
      setUserLoc({ lat: loc.lat, lng: loc.lng });
      setPhase("map");
    } catch (e) {
      fail(e);
    }
  }, [loadMeta, fail]);

  const reloadLocation = useCallback(async () => {
    setReloading(true);
    try {
      const loc = await getCurrentPosition();
      setUserLoc({ lat: loc.lat, lng: loc.lng });
      toast.success("Đã cập nhật vị trí");
    } catch (e) {
      toast.error(extractErrorMessage(e, "Không lấy được vị trí"));
    } finally {
      setReloading(false);
    }
  }, []);

  const complete = useCallback(async () => {
    try {
      setPhase("completing");
      setBusyMsg("Đang hoàn tất chấm công...");
      await completeCheckinApi();
      queryClient.invalidateQueries({ queryKey: ["emp", "eligibility"] });
      queryClient.invalidateQueries({ queryKey: ["emp", "calendar"] });
      setPhase("success");
    } catch (e) {
      fail(e);
    }
  }, [queryClient, fail]);

  const doCheckIn = useCallback(async () => {
    try {
      let checkinPlaceId;
      if (validPlaces.length) {
        const sel = validPlaces.find((p) => p._id === selectedId) || validPlaces[0];
        checkinPlaceId = sel._id;
      } else if (defaultId) {
        checkinPlaceId = undefined; // để BE dùng địa điểm mặc định
      } else {
        toast.error("Bạn chưa ở gần địa điểm chấm công nào");
        return;
      }

      setPhase("verifying");
      setBusyMsg("Đang xác thực địa điểm...");
      await startCheckinApi();
      const verified = await verifyCheckinPlaceApi({
        location: { lat: userLoc.lat, lng: userLoc.lng },
        checkinPlaceId,
      });
      setPlace(verified);

      const type = verified?.checkInTypes?.[0];
      if (!type) await complete();
      else if (type === CHECK_IN_TYPE.IMAGE) setPhase("image");
      else if (type === CHECK_IN_TYPE.QRCODE) setPhase("qr");
      else if (type === CHECK_IN_TYPE.BSSID) setPhase("bssid");
      else await complete();
    } catch (e) {
      fail(e);
    }
  }, [validPlaces, selectedId, defaultId, userLoc, complete, fail]);

  const onImageCaptured = useCallback(
    async (file) => {
      try {
        setPhase("completing");
        setBusyMsg("Đang tải ảnh & xác thực...");
        const data = await uploadImageApi(file);
        const filePath = typeof data === "string" ? data : data?.filePath;
        await verifyCheckinImageApi(filePath);
        await complete();
      } catch (e) {
        fail(e);
      }
    },
    [complete, fail]
  );

  const onQrDetected = useCallback(
    async (code) => {
      try {
        setPhase("completing");
        setBusyMsg("Đang xác thực mã QR...");
        await verifyCheckinQrCodeApi(code);
        await complete();
      } catch (e) {
        fail(e);
      }
    },
    [complete, fail]
  );

  // Mở thẳng bản đồ khi vào màn (bỏ bước "Bắt đầu chấm công") - khớp app
  useEffect(() => {
    begin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = (
    <>
      {phase === "map" && userLoc ? (
        <Box position="relative" h={{ base: "calc(100dvh - 52px)", md: "72vh" }}>
          <CheckInMap
            center={[userLoc.lat, userLoc.lng]}
            radius={radius}
            places={places}
            validIds={validIds}
            selectedId={selectedId}
            onSelectPlace={(p) => setSelectedId(p._id)}
            height="100%"
            borderRadius="0px"
          />
          <Box position="absolute" left="16px" right="16px" bottom="20px" zIndex={1000}>
            <Button
              w="100%"
              h="52px"
              borderRadius="10px"
              isDisabled={!validPlaces.length && !defaultId}
              bgGradient={BLUE_GRADIENT}
              color="white"
              fontWeight={700}
              _hover={{ bgGradient: BLUE_GRADIENT, opacity: 0.92 }}
              _disabled={{ opacity: 0.45, cursor: "not-allowed" }}
              boxShadow="0 6px 16px rgba(33,104,218,0.4)"
              onClick={doCheckIn}
            >
              Chấm công
            </Button>
            <Flex justify="center" mt="8px">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<FiCrosshair />}
                isLoading={reloading}
                onClick={reloadLocation}
                color={PRIMARY}
                _hover={{ bg: "transparent" }}
              >
                Tải lại vị trí (Chính xác)
              </Button>
            </Flex>
          </Box>
        </Box>
      ) : (
      <Box px="16px" pt="16px">
      <Card p={{ base: "0", md: "0" }} overflow="hidden">
        {/* Progress step ở các bước xác thực (khớp app) */}
        {(phase === "image" || phase === "qr" || phase === "bssid") && (
          <ProgressStep
            step2Label={
              phase === "image" ? "Đăng tải ảnh" : phase === "qr" ? "Quét QR code" : "Kết nối WiFi"
            }
          />
        )}

        <Box p="16px">
          {/* BUSY */}
          {(phase === "locating" || phase === "verifying" || phase === "completing") && (
            <Loading label={busyMsg} />
          )}

          {/* IMAGE */}
          {phase === "image" && (
            <Flex direction="column" align="center" gap="24px" pt="8px">
              <Text fontSize="18px" fontWeight={800} color="#08142D" textAlign="center">
                Ảnh chụp check in
              </Text>
              <Box
                w="285px"
                maxW="100%"
                h="285px"
                border="1px dashed #DAE0EB"
                borderRadius="16px"
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CameraCapture onCapture={onImageCaptured} />
              </Box>
            </Flex>
          )}

          {/* QR */}
          {phase === "qr" && (
            <Flex direction="column" align="center" gap="16px">
              <Text fontSize="16px" fontWeight={700} color="#08142D" textAlign="center">
                Quét mã QR tại {place?.identifier || place?.name}
              </Text>
              <QrScanner onDetected={onQrDetected} />
            </Flex>
          )}

          {/* BSSID */}
          {phase === "bssid" && (
            <Flex direction="column" align="center" gap="14px" py="30px">
              <Text fontSize="15px" color="#08142D" textAlign="center" fontWeight={600}>
                Xin vui lòng kết nối wifi
              </Text>
              <Flex
                align="center"
                gap="8px"
                bg={PRIMARY}
                color="white"
                px="18px"
                h="44px"
                borderRadius="8px"
                fontWeight={700}
              >
                <Box as={FiWifi} />
                Kết nối WiFi
              </Flex>
              <Text fontSize="13px" color="var(--color-label-employee)" textAlign="center">
                Trình duyệt web không đọc được BSSID WiFi vì lý do bảo mật. Vui lòng dùng app trên
                điện thoại để chấm công tại địa điểm này.
              </Text>
              <Button
                variant="outline"
                borderColor={PRIMARY}
                color={PRIMARY}
                _hover={{ bg: "#EAF2FF" }}
                onClick={() => setPhase("map")}
              >
                Quay lại bản đồ
              </Button>
            </Flex>
          )}

          {/* SUCCESS */}
          {phase === "success" && (
            <Flex direction="column" align="center" gap="14px" py="12px">
              <Flex
                w="80px"
                h="80px"
                borderRadius="full"
                bg="#E9FFF1"
                align="center"
                justify="center"
                color="#18C758"
                fontSize="40px"
              >
                <Box as={FiCheckCircle} />
              </Flex>
              <Text fontSize="18px" fontWeight={700} color="#18C758">
                Chấm công thành công!
              </Text>
              <Text fontSize="13px" color="var(--color-label-employee)">
                {place?.identifier || place?.name || ""}
              </Text>
              <Flex gap="10px" w="100%">
                <Button
                  flex="1"
                  variant="outline"
                  borderColor={PRIMARY}
                  color={PRIMARY}
                  _hover={{ bg: "#EAF2FF" }}
                  onClick={() => navigate(EMP_BASE_PATH)}
                >
                  Về trang chủ
                </Button>
                <Button
                  flex="1"
                  bgGradient={BLUE_GRADIENT}
                  color="white"
                  _hover={{ bgGradient: BLUE_GRADIENT, opacity: 0.92 }}
                  onClick={() => navigate(`${EMP_BASE_PATH}/chuyen-can`)}
                >
                  Xem chuyên cần
                </Button>
              </Flex>
            </Flex>
          )}

          {/* ERROR */}
          {phase === "error" && (
            <Flex direction="column" align="center" gap="14px" py="12px">
              <Flex
                w="64px"
                h="64px"
                borderRadius="full"
                bg="#FFF0F0"
                align="center"
                justify="center"
                color="#EF2E14"
                fontSize="30px"
              >
                <Box as={FiAlertTriangle} />
              </Flex>
              <Text fontSize="14px" color="#EF2E14" textAlign="center" fontWeight={600}>
                {error}
              </Text>
              <Button
                leftIcon={<FiRefreshCw />}
                bgGradient={BLUE_GRADIENT}
                color="white"
                _hover={{ bgGradient: BLUE_GRADIENT, opacity: 0.92 }}
                onClick={begin}
              >
                Thử lại
              </Button>
            </Flex>
          )}
        </Box>
      </Card>
      </Box>
      )}
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  // Giữ nguyên toàn bộ luồng chấm công, chỉ đổi khung trang sang style admin.
  if (isDesktop) {
    return (
      <DesktopPage
        title="Chấm công"
        right={
          <DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/chuyen-can`)} />
        }
      >
        <DesktopPanel p="0" overflow="hidden">
          {content}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto">
      <AppBar title="Chấm công" onBack={() => navigate(`${EMP_BASE_PATH}/chuyen-can`)} />
      {content}
    </Box>
  );
};

export default CheckIn;
